import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, errMsg } from "../lib/api";
import { Skeleton } from "../components/Skeleton";

const ROOMS = [
  { key: "balboa", label: "Balboa Recording Studio" },
  { key: "bungalow", label: "Balboa Bungalow" },
  { key: "either", label: "Not sure yet" },
];

const empty = {
  name: "",
  email: "",
  phone: "",
  room: "balboa",
  datesTBD: false,
  startDate: "",
  endDate: "",
  project: "",
  notes: "",
  website: "",
};

export default function Contact() {
  const [params] = useSearchParams();
  const preset = params.get("room");
  const [form, setForm] = useState({
    ...empty,
    room: ROOMS.some((r) => r.key === preset) ? preset : "balboa",
  });
  const [settings, setSettings] = useState(null);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setState("sending");
    try {
      await api.post("/contact", form);
      if (typeof window.gtag === "function") {
        window.gtag("event", "generate_lead", {
          method: "contact_form",
          room: form.room,
        });
      }
      setState("sent");
      setForm({ ...empty, room: form.room });
    } catch (err) {
      setError(errMsg(err));
      setState("idle");
    }
  };

  return (
    <div data-testid="contact-page">
      <div className="wrap page-head">
        <p className="label" style={{ color: "var(--orange)" }}>Contact / Book</p>
        <h1 className="disp" style={{ marginTop: 12 }}>
          <span className="misreg" data-text="Let's Book It">Let's Book It</span>
        </h1>
        <p className="measure" style={{ marginTop: 20 }}>
          Tell us what you're making and when you want the room. We answer every message ourselves, usually the
          same day.
        </p>
      </div>

      <div className="wrap">
        {settings?.bannerOn && settings?.bannerText && (
          <div className="banner" data-testid="availability-banner">
            {settings.bannerText}
          </div>
        )}

        <div className="two-col">
          <div>
            {state === "sent" ? (
              <div className="confirm" data-testid="contact-confirmation">
                — Got it. Your request is in. We'll get back to you shortly. —
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form">
                <label className="field">
                  <span>Name *</span>
                  <input className="inp" required value={form.name} onChange={set("name")} data-testid="contact-name" />
                </label>
                <label className="field">
                  <span>Email *</span>
                  <input
                    className="inp"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    data-testid="contact-email"
                  />
                </label>
                <label className="field">
                  <span>Which room? *</span>
                  <div className="room-pick" data-testid="contact-room-pick">
                    {ROOMS.map((r) => (
                      <button
                        type="button"
                        key={r.key}
                        className={`room-opt${form.room === r.key ? " active" : ""}`}
                        onClick={() => setForm({ ...form, room: r.key })}
                        aria-pressed={form.room === r.key}
                        data-testid={`contact-room-${r.key}`}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                </label>
                <label className="field">
                  <span>Phone (optional)</span>
                  <input className="inp" value={form.phone} onChange={set("phone")} data-testid="contact-phone" />
                </label>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 16 }}>
                  <label className="field">
                    <span>Session start</span>
                    <input
                      className="inp"
                      type="date"
                      value={form.startDate}
                      disabled={form.datesTBD}
                      onChange={set("startDate")}
                      data-testid="contact-start-date"
                    />
                  </label>
                  <label className="field">
                    <span>Session end</span>
                    <input
                      className="inp"
                      type="date"
                      value={form.endDate}
                      disabled={form.datesTBD}
                      onChange={set("endDate")}
                      data-testid="contact-end-date"
                    />
                  </label>
                </div>
                <div className="room-pick" style={{ marginTop: -8, marginBottom: 22 }}>
                  <button
                    type="button"
                    className={`room-opt${form.datesTBD ? " active" : ""}`}
                    aria-pressed={form.datesTBD}
                    onClick={() =>
                      setForm({
                        ...form,
                        datesTBD: !form.datesTBD,
                        startDate: "",
                        endDate: "",
                      })
                    }
                    data-testid="contact-dates-tbd"
                  >
                    Dates — not sure yet
                  </button>
                </div>
                <label className="field">
                  <span>What are you working on? *</span>
                  <textarea
                    className="inp"
                    required
                    value={form.project}
                    onChange={set("project")}
                    data-testid="contact-project"
                  />
                </label>
                <label className="field">
                  <span>Anything else? (optional)</span>
                  <textarea className="inp" value={form.notes} onChange={set("notes")} data-testid="contact-notes" />
                </label>

                <div className="hp">
                  <label>
                    Website
                    <input tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
                  </label>
                </div>

                {error && (
                  <p className="mono" style={{ color: "var(--orange)", fontSize: 13 }} data-testid="contact-error">
                    {error}
                  </p>
                )}

                <button className="btn" type="submit" disabled={state === "sending"} data-testid="contact-submit">
                  {state === "sending" ? "Sending…" : "Send It →"}
                </button>
              </form>
            )}
          </div>

          <div>
            {loading ? (
              <div className="card" data-testid="rates-skeleton">
                <Skeleton h={12} w="30%" />
                <div style={{ marginTop: 14 }}>
                  <Skeleton h={14} w="80%" style={{ marginBottom: 8 }} />
                  <Skeleton h={14} w="65%" style={{ marginBottom: 8 }} />
                  <Skeleton h={14} w="45%" />
                </div>
              </div>
            ) : (
              settings?.rates && (
              <div className="card" data-testid="rates-block">
                <p className="label" style={{ color: "var(--orange)", marginBottom: 12 }}>Rates</p>
                <pre
                  className="mono"
                  style={{ whiteSpace: "pre-wrap", fontSize: 14, margin: 0 }}
                >
                  {settings.rates}
                </pre>
              </div>
              )
            )}
            <div className="misreg-rule" style={{ margin: "32px 0" }} />
            <p className="mono" style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: "0.1em" }}>
              Glassell Park, Los Angeles
              <br />
              Exact address sent on booking
            </p>
          </div>
        </div>

        <p
          className="mono"
          style={{ marginTop: 56, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.12em", opacity: 0.7 }}
          data-testid="calendar-note"
        >
          Calendar availability sync coming soon.
        </p>
      </div>
    </div>
  );
}
